import React, { useState, useEffect } from 'react';
import { useGameStore } from '../../store/gameStore';

interface Props {
  send: (action: string, data?: object) => void;
}

interface Character {
  id: string;
  name: string;
  image: string;
  color: string;
  specialty?: string;
  trait?: string;
}

const CUSTOMER_CHARACTERS: Character[] = [
  { id: 'emma', name: 'Emma Chen', image: '/images/characters/sprites/girl_1_idle.png', color: '#e91e63', trait: '❤️ Fruity, Vanilla' },
  { id: 'sarah', name: 'Sarah Kim', image: '/images/characters/sprites/girl_2_idle.png', color: '#9c27b0', trait: '❤️ Everything!' },
  { id: 'margaret', name: 'Margaret Thompson', image: '/images/characters/sprites/girl_3_idle.png', color: '#795548', trait: '❤️ Chocolate, Sweet' },
  { id: 'harold', name: 'Harold Brown', image: '/images/characters/sprites/city_man_1_idle.png', color: '#ff9800', trait: '❤️ Comfort Food' },
  { id: 'robert', name: 'Robert Garcia', image: '/images/characters/sprites/city_man_2_idle.png', color: '#4caf50', trait: '❤️ Nutty, Grain' },
  { id: 'james', name: 'James Wilson', image: '/images/characters/sprites/city_man_3_idle.png', color: '#2196f3', trait: '❤️ Savory, Spicy' },
];

const CHEF_CHARACTERS: Character[] = [
  { id: 'magnus', name: 'Chef Magnus', image: '/images/characters/sprites/chibi_wizard_idle.png', color: '#673ab7', specialty: 'Desserts' },
  { id: 'azure', name: 'Chef Azure', image: '/images/characters/sprites/chibi_swordsman_idle.png', color: '#3f51b5', specialty: 'Quick Orders' },
  { id: 'scarlet', name: 'Chef Scarlet', image: '/images/characters/sprites/chibi_archer_idle.png', color: '#f44336', specialty: 'Complex Recipes' },
];

export function CharacterSelectionScreen({ send }: Props) {
  const { role, players, setPhase } = useGameStore();
  const [selectedCharacter, setSelectedCharacter] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(45);

  const characters = role === 'chef' ? CHEF_CHARACTERS : CUSTOMER_CHARACTERS;

  // Get already selected characters
  const takenCharacters = players
    .filter(p => p.characterId)
    .map(p => p.characterId);

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Auto-select first available if time runs out
          if (!selectedCharacter) {
            const available = characters.find(c => !takenCharacters.includes(c.id));
            if (available) {
              handleSelectCharacter(available.id);
            }
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [selectedCharacter, characters, takenCharacters]);

  const handleSelectCharacter = (characterId: string) => {
    if (takenCharacters.includes(characterId)) return;
    setSelectedCharacter(characterId);
    send('selectCharacter', { characterId });
  };

  const handleConfirm = () => {
    if (selectedCharacter) {
      // Start the first meal period
      send('startMealPeriod', { mealPeriod: 'breakfast' });
      setPhase('gameplay');
    }
  };

  return (
    <div className="screen">
      {/* Timer */}
      <div style={{
        position: 'absolute',
        top: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: timeLeft <= 10 ? 'var(--ltc-danger)' : 'var(--ltc-bg-dark)',
        color: 'var(--ltc-accent-glow)',
        padding: '8px 24px',
        border: 'var(--ltc-border)',
        fontFamily: 'var(--ltc-font-display)',
        fontSize: 20,
      }}>
        {timeLeft}s
      </div>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 32 }}>
        <h1 className="title-pixel" style={{ fontSize: 24, marginBottom: 8 }}>
          Choose Your Character
        </h1>
        <p className="subtitle">
          {role === 'chef' ? 'Pick your chef persona' : 'Who are you today?'}
        </p>
      </div>

      {/* Character Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 16,
        maxWidth: 600,
        width: '100%',
        marginBottom: 32,
      }}>
        {characters.map((character) => {
          const isTaken = takenCharacters.includes(character.id);
          const isSelected = selectedCharacter === character.id;

          return (
            <button
              key={character.id}
              onClick={() => handleSelectCharacter(character.id)}
              disabled={isTaken}
              style={{
                padding: 16,
                background: isSelected
                  ? character.color
                  : isTaken
                  ? 'var(--ltc-bg-tertiary)'
                  : 'var(--ltc-bg-card)',
                border: isSelected
                  ? '4px solid var(--ltc-primary-dark)'
                  : 'var(--ltc-border)',
                boxShadow: isSelected
                  ? '4px 4px 0 rgba(44, 24, 16, 0.4)'
                  : isTaken
                  ? 'none'
                  : 'var(--ltc-shadow)',
                cursor: isTaken ? 'not-allowed' : 'pointer',
                opacity: isTaken ? 0.5 : 1,
                transition: 'all 0.2s ease',
                transform: isSelected ? 'translate(-2px, -2px)' : 'none',
              }}
            >
              <div 
                style={{
                  width: 72,
                  height: 72,
                  backgroundImage: `url(${character.image})`,
                  backgroundSize: 'auto 100%',
                  backgroundPosition: 'left center',
                  backgroundRepeat: 'no-repeat',
                  imageRendering: 'pixelated',
                  marginBottom: 8,
                  filter: isTaken ? 'grayscale(1)' : 'none',
                }}
              />
              <div style={{
                fontWeight: 600,
                color: isSelected ? 'white' : 'var(--ltc-text-primary)',
                marginBottom: 4,
                fontSize: 13,
              }}>
                {character.name}
              </div>
              {character.specialty && (
                <div style={{
                  fontSize: 11,
                  color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--ltc-text-muted)',
                }}>
                  ⭐ {character.specialty}
                </div>
              )}
              {character.trait && (
                <div style={{
                  fontSize: 10,
                  color: isSelected ? 'rgba(255,255,255,0.8)' : 'var(--ltc-text-muted)',
                }}>
                  {character.trait}
                </div>
              )}
              {isTaken && (
                <div style={{
                  fontSize: 10,
                  color: 'var(--ltc-danger)',
                  marginTop: 4,
                }}>
                  Taken
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Confirm Button */}
      {selectedCharacter && (
        <button
          className="btn btn-accent btn-large"
          onClick={handleConfirm}
          style={{ minWidth: 200 }}
        >
          Let's Play! 🎮
        </button>
      )}
    </div>
  );
}
